<script>
import { useAlert } from 'dashboard/composables';
import ReportFilters from './components/ReportFilters.vue';
import AttributionTable from './components/AttributionTable.vue';
import DetailedAttributionTable from './components/DetailedAttributionTable.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';

export default {
  name: 'AttributionReports',
  components: {
    ReportFilters,
    AttributionTable,
    DetailedAttributionTable,
    ReportHeader,
  },
  data() {
    return {
      from: 0,
      to: 0,
      userIds: [],
      isLoading: false,
      isLoadingDetailed: false,
      summaryData: [],
      detailedData: [],
    };
  },
  computed: {
    requestPayload() {
      const payload = {
        since: this.from,
        until: this.to,
      };

      if (this.userIds.length === 1) {
        payload.user_id = this.userIds[0];
      } else if (this.userIds.length > 1) {
        payload.user_ids = this.userIds;
      }

      return payload;
    },
  },
  methods: {
    async getAllData() {
      try {
        this.isLoading = true;
        this.isLoadingDetailed = true;
        await Promise.all([this.getSummary(), this.getDetailed()]);
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      } finally {
        this.isLoading = false;
        this.isLoadingDetailed = false;
      }
    },
    async getSummary() {
      try {
        const response = await this.$store.dispatch(
          'attributionReports/getSummary',
          this.requestPayload
        );
        this.summaryData = response || [];
      } catch {
        useAlert(this.$t('ATTRIBUTION_REPORTS.SUMMARY_FETCH_FAILED'));
      }
    },
    async getDetailed() {
      try {
        const response = await this.$store.dispatch(
          'attributionReports/getDetailed',
          this.requestPayload
        );
        this.detailedData = response || [];
      } catch {
        useAlert(this.$t('ATTRIBUTION_REPORTS.DETAILED_FETCH_FAILED'));
      }
    },
    async downloadReports() {
      try {
        await this.$store.dispatch('attributionReports/downloadSummaryReport', {
          fileName: generateFileName({
            type: 'attribution-summary',
            to: this.to,
          }),
          ...this.requestPayload,
        });
      } catch {
        useAlert(this.$t('ATTRIBUTION_REPORTS.DOWNLOAD_SUMMARY_FAILED'));
      }
    },
    async downloadDetailedReports() {
      try {
        await this.$store.dispatch(
          'attributionReports/downloadDetailedReport',
          {
            fileName: generateFileName({
              type: 'attribution-detailed',
              to: this.to,
            }),
            ...this.requestPayload,
          }
        );
      } catch {
        useAlert(this.$t('ATTRIBUTION_REPORTS.DOWNLOAD_DETAILED_FAILED'));
      }
    },
    onFilterChange({ from, to, agents }) {
      this.from = from;
      this.to = to;
      this.userIds = agents ? agents.map(agent => agent.id) : [];
      this.getAllData();
    },
  },
};
</script>

<template>
  <div>
    <div class="mt-4 mb-4">
      <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
        {{ $t('ATTRIBUTION_REPORTS.FILTERS') }}
      </h2>
      <ReportFilters
        filter-type="agents"
        :show-group-by="false"
        :show-business-hours="false"
        show-entity-filter
        disable-entity-routing
        @filter-change="onFilterChange"
      />
    </div>

    <ReportHeader :header-title="$t('ATTRIBUTION_REPORTS.SUMMARY_HEADER')">
      <button
        class="button nice"
        :disabled="isLoading || !summaryData.length"
        @click="downloadReports"
      >
        {{ $t('ATTRIBUTION_REPORTS.DOWNLOAD') }}
      </button>
    </ReportHeader>

    <div class="flex flex-col gap-4">
      <AttributionTable
        v-if="from && to"
        :data="summaryData"
        :is-loading="isLoading"
      />

      <div v-else class="flex items-center justify-center p-8">
        <p class="text-slate-600 dark:text-slate-400">
          {{ $t('ATTRIBUTION_REPORTS.SELECT_PERIOD') }}
        </p>
      </div>

      <div v-if="from && to" class="mt-8">
        <ReportHeader :header-title="$t('ATTRIBUTION_REPORTS.DETAILED_HEADER')">
          <button
            class="button nice"
            :disabled="isLoadingDetailed || !detailedData.length"
            @click="downloadDetailedReports"
          >
            {{ $t('ATTRIBUTION_REPORTS.DOWNLOAD') }}
          </button>
        </ReportHeader>
        <DetailedAttributionTable
          :data="detailedData"
          :is-loading="isLoadingDetailed"
        />
      </div>
    </div>
  </div>
</template>
